import type { ChangeEvent } from 'react';
import type { EtatRequete } from '../../api/requete.js';
import type { DonneesClerc, FichierADeposer } from './chargement.js';
import { dossierRetenu } from './chargement.js';
import { CoqueEspace, styleAction, styleLigne, styleSecondaire, styleSection } from './commun.js';
import { BlocEstimation, type EstimationExterne } from './estimation.js';
import { libelleInterrogation, libelleStatutInterrogation } from './format.js';

export interface PropsVueClerc {
  etat: EtatRequete<DonneesClerc>;
  /** Adresse de la copie en lecture seule de l’ancien Trello (donnée de configuration, jamais en dur). */
  lienCopieTrello?: string | null;
  estimation?: EstimationExterne;
  onMarquer?: (interrogationIndex: number, statut: 'envoyee' | 'revenue') => void;
  onDeposer?: (fichier: FichierADeposer) => void;
}

const rien = (): void => undefined;

export function VueEspaceClerc({ etat, lienCopieTrello = null, estimation, onMarquer = rien, onDeposer = rien }: PropsVueClerc) {
  return (
    <CoqueEspace
      ecran="X11"
      titre="Espace clerc de notaire"
      etat={etat}
      vide="Aucun dossier ne vous a encore été transmis par le Cabinet MAAT. Vous serez prévenu dès qu’un dossier sera prêt pour ouverture."
      enfants={(d) => {
        const retenu = dossierRetenu(d.dossiers);
        const prets = d.dossiers.filter((x) => x.readyForOpening);
        const autres = d.dossiers.filter((x) => !x.readyForOpening);
        const enAttente = d.interrogations.filter((i) => i.status === 'envoyee').length;
        const manquantes = retenu?.missingPieces ?? [];
        return (
          <>
            <p style={styleSecondaire}>Dossiers transmis par le Cabinet MAAT</p>
            <p>
              {lienCopieTrello ? (
                <a href={lienCopieTrello} style={{ ...styleAction, display: 'inline-flex', alignItems: 'center' }}>
                  Voir la copie de l’ancien Trello (lecture seule)
                </a>
              ) : (
                <span style={styleSecondaire}>La copie de l’ancien Trello n’est pas encore disponible.</span>
              )}
            </p>

            <section aria-labelledby="clerc-synthese" style={styleSection}>
              <h2 id="clerc-synthese">En un coup d’œil</h2>
              <p style={styleLigne}>
                <span>Prêts pour ouverture</span>
                <strong>{prets.length}</strong>
              </p>
              <p style={styleLigne}>
                <span>Interrogations en attente de retour</span>
                <strong>{enAttente}</strong>
              </p>
            </section>

            <section aria-labelledby="clerc-prets" style={styleSection}>
              <h2 id="clerc-prets">Dossiers prêts</h2>
              {prets.map((p) => (
                <article key={p.reference}>
                  <h3>{p.title}</h3>
                  <p style={styleSecondaire}>Prêt pour ouverture · tableau des vivants à {p.completeness} %</p>
                </article>
              ))}
              {prets.length === 0 ? <p>Aucun dossier n’est encore prêt pour ouverture.</p> : null}
            </section>

            <section aria-labelledby="clerc-interrogations" style={styleSection}>
              <h2 id="clerc-interrogations">Interrogations à lancer{retenu ? ` · ${retenu.title}` : ''}</h2>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {d.interrogations.map((i, index) => (
                  <li key={i.kind + index} style={styleLigne}>
                    <span>
                      {libelleInterrogation(i.kind)} <span style={styleSecondaire}>{libelleStatutInterrogation(i)}</span>
                    </span>
                    {i.status === 'a_envoyer' ? (
                      <button type="button" style={styleAction} onClick={() => onMarquer(index, 'envoyee')}>
                        Marquer comme envoyée
                      </button>
                    ) : null}
                    {i.status === 'envoyee' ? (
                      <button type="button" style={styleAction} onClick={() => onMarquer(index, 'revenue')}>
                        Marquer le retour reçu
                      </button>
                    ) : null}
                  </li>
                ))}
              </ul>
            </section>

            <section aria-labelledby="clerc-retour" style={styleSection}>
              <h2 id="clerc-retour">Retours et tableaux</h2>
              <label style={{ display: 'block' }}>
                Déposer un retour
                <input
                  type="file"
                  style={{ display: 'block', minHeight: 44 }}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    const f = e.target.files?.[0];
                    if (f) onDeposer({ nom: f.name, mime: f.type || 'application/octet-stream', taille: f.size, octets: f });
                  }}
                />
              </label>
            </section>

            <section aria-labelledby="clerc-pieces" style={styleSection}>
              <h2 id="clerc-pieces">Pièces du dossier</h2>
              <ul>
                {d.pieces.map((p, index) => (
                  <li key={p.title + index}>{p.title}</li>
                ))}
                {manquantes.map((m, index) => (
                  <li key={m.label + index} style={styleSecondaire}>
                    À obtenir : {m.label}
                  </li>
                ))}
              </ul>
            </section>

            {autres.length > 0 ? (
              <section aria-labelledby="clerc-autres" style={styleSection}>
                <h2 id="clerc-autres">Autres dossiers</h2>
                <ul>
                  {autres.map((a) => (
                    <li key={a.reference}>
                      {a.title} <span style={styleSecondaire}>· complétude {a.completeness} %</span>
                      {a.missingPieces.length > 0 ? (
                        <ul>
                          {a.missingPieces.map((m, index) => (
                            <li key={m.label + index} style={styleSecondaire}>
                              À obtenir : {m.label}
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            {estimation ? <BlocEstimation role="clerc" reglage={estimation.reglage} lignes={estimation.lignes} /> : null}

            <p style={styleSecondaire}>Vous ne voyez ni les notes internes du cabinet, ni les échanges WhatsApp avec les familles.</p>
          </>
        );
      }}
    />
  );
}
